import type { AutocompleteModel, FieldDef, FormBuilderWidget } from '../types';

// The palette shown in the Library › Fields tab. Order mirrors the design.
export interface FieldTypeMeta {
  widget: FormBuilderWidget;
  label: string;
  // A short human badge label (e.g. "LONG TEXT") shown on canvas rows.
  badge: string;
  // Placeholder preview text shown in the field's disabled canvas preview.
  preview: string;
}

export const FIELD_TYPES: FieldTypeMeta[] = [
  { widget: 'text', label: 'Text', badge: 'TEXT', preview: 'Short answer…' },
  { widget: 'textarea', label: 'Long Text', badge: 'LONG TEXT', preview: 'Long answer…' },
  { widget: 'number', label: 'Number', badge: 'NUMBER', preview: '0' },
  { widget: 'date', label: 'Date', badge: 'DATE', preview: 'mm / dd / yyyy' },
  { widget: 'checkbox', label: 'Checkbox', badge: 'CHECKBOX', preview: '☐ Yes / no' },
  { widget: 'select', label: 'Select', badge: 'SELECT', preview: 'Choose one…' },
  { widget: 'radio', label: 'Radio', badge: 'RADIO', preview: '○ Option A   ○ Option B' },
  {
    widget: 'autocomplete',
    label: 'Autocomplete',
    badge: 'AUTOCOMPLETE',
    preview: 'Search…'
  },
  { widget: 'html_content', label: 'Text to Read', badge: 'CONTENT', preview: 'Text to read…' },
  { widget: 'image', label: 'Image', badge: 'IMAGE', preview: 'Image' },
  {
    widget: 'section_header',
    label: 'Section Header',
    badge: 'HEADER',
    preview: 'Section header'
  }
];

// The records an autocomplete field can search. Keys must stay in step with
// the autocomplete registry, which resolves a widget to its data source.
export const AUTOCOMPLETE_MODELS: { value: AutocompleteModel; label: string }[] = [
  { value: 'staff', label: 'Staff' },
  { value: 'person', label: 'People' },
  { value: 'organization', label: 'Organizations' },
  { value: 'program', label: 'Programs' },
  { value: 'phase_type', label: 'Phase types' }
];

export const autocompleteModelLabel = (model: AutocompleteModel | undefined): string =>
  AUTOCOMPLETE_MODELS.find((m) => m.value === (model ?? 'staff'))?.label ?? 'Staff';

const BY_WIDGET: Record<FormBuilderWidget, FieldTypeMeta> = Object.fromEntries(
  FIELD_TYPES.map((meta) => [meta.widget, meta])
) as Record<FormBuilderWidget, FieldTypeMeta>;

export const fieldTypeMeta = (widget: FormBuilderWidget): FieldTypeMeta => BY_WIDGET[widget];

// Badge palette keyed by badge label — tailwind class pairs [bg, text] matching
// the design's per-widget colouring.
const BADGE_CLASSES: Record<string, string> = {
  TEXT: 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300',
  'LONG TEXT': 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300',
  NUMBER: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/30 dark:text-cyan-300',
  DATE: 'bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-300',
  CHECKBOX: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-300',
  SELECT: 'bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-300',
  RADIO: 'bg-violet-50 text-violet-700 dark:bg-violet-950/30 dark:text-violet-300',
  AUTOCOMPLETE: 'bg-pink-50 text-pink-700 dark:bg-pink-950/30 dark:text-pink-300',
  CONTENT:
    'bg-uniform-gray-100 text-uniform-gray-600 dark:bg-uniform-gray-800 dark:text-uniform-gray-300',
  IMAGE:
    'bg-uniform-gray-100 text-uniform-gray-600 dark:bg-uniform-gray-800 dark:text-uniform-gray-300',
  HEADER:
    'bg-uniform-gray-100 text-uniform-gray-600 dark:bg-uniform-gray-800 dark:text-uniform-gray-300'
};

export const badgeClasses = (widget: FormBuilderWidget): string =>
  BADGE_CLASSES[fieldTypeMeta(widget).badge] ??
  'bg-uniform-gray-100 text-uniform-gray-600 dark:bg-uniform-gray-800 dark:text-uniform-gray-300';

export const previewText = (field: FieldDef): string => {
  if (field.widget === 'radio' || field.widget === 'select') {
    const opts = (field.options ?? []).map((o) => o.label).slice(0, 3);
    if (opts.length) {
      return field.widget === 'radio' ? opts.map((o) => `○ ${o}`).join('   ') : opts.join(' · ');
    }
  }
  return fieldTypeMeta(field.widget).preview;
};
